import { IHeaderParams } from "ag-grid-community";
import { EllipseVerticalIcon, EyeSlashIcon, TrashIcon } from "../Icons";

import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CustomModal from "./CustomModal";
import { CompleteJson } from "../../types/DealTracker";

export default function MyInnerHeaderComponent(
  props: IHeaderParams & { deleteColumn: (colId: keyof CompleteJson) => void }
) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useLayoutEffect(() => {
    if (isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  const onClose = () => {
    setIsOpen(false);
  };

  const handleHideCol = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.api.setColumnsVisible([props.column.getColId()], false);
  };

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleDeleteCol = () =>
    props.deleteColumn(props.column.getId() as keyof CompleteJson);

  return (
    <>
      <div className="flex items-center gap-1">
        <button
          ref={btnRef}
          className="rounded hover:bg-gray-200"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          <EllipseVerticalIcon className="size-5" />
        </button>
        <span>{props.displayName}</span>
      </div>

      <HeaderPopup
        isOpen={isOpen}
        pos={pos}
        onClose={onClose}
        triggerRef={btnRef}
      >
        <div className="p-2">
          <button
            className="w-full px-2 py-1 text-sm flex items-center gap-1 rounded hover:bg-gray-100"
            onClick={handleHideCol}
          >
            <EyeSlashIcon className="size-4" />
            Hide column
          </button>
          <button
            className="w-full px-2 py-1 text-sm flex items-center gap-1 rounded hover:bg-red-100"
            onClick={() => {
              toggleModal();
              onClose();
            }}
          >
            <TrashIcon className="size-4" />
            Delete column
          </button>
        </div>
      </HeaderPopup>

      <CustomModal open={isModalOpen} onClose={toggleModal}>
        <div className="space-y-4">
          <h1 className="font-medium text-xl text-gray-950">
            ⚠️ You are about to delete important data.
          </h1>
          <p className="text-sm text-gray-900">
            Please confirm that you want to permanently remove this item.
          </p>

          <div className="flex gap-4">
            <button
              className="w-full bg-red-500 text-white p-2 cursor-pointer rounded-xl"
              onClick={handleDeleteCol}
            >
              Yes
            </button>
            <button
              className="w-full text-gray-900 p-2 cursor-pointer border border-gray-900 rounded-xl"
              onClick={toggleModal}
            >
              No
            </button>
          </div>
        </div>
      </CustomModal>
    </>
  );
}

function HeaderPopup({
  isOpen,
  pos,
  children,
  onClose,
  triggerRef,
}: {
  isOpen: boolean;
  pos: { left: number; top: number };
  children: ReactNode;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement>;
}) {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={popupRef}
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        zIndex: 100,
      }}
      className="rounded-md border bg-white shadow-lg"
    >
      {children}
    </div>,
    document.body
  );
}
